import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target as Node) &&
        !(e.target as HTMLElement).closest('.mobile-hamburger-btn')
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 h-16 flex items-center transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-10 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-foreground-950 font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap cursor-pointer"
            >
              Itaobuy<span className="text-primary-500">.</span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className="px-3 py-1.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors rounded-lg cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <Link
                to="/articoli/shopping-internazionale-guida"
                className="px-3 py-1.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors rounded-lg cursor-pointer whitespace-nowrap"
              >
                Guida Acquisti
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div ref={dropdownRef} className="relative hidden md:block">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-700 hover:border-background-300/60 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>{t('nav.categories')}</span>
                {isDropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-foreground-500"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                )}
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200/60 rounded-2xl overflow-hidden z-50 shadow-sm">
                  <div className="max-h-[420px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-accent-50/50 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {cat.icon && (
                          <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                            <i className={`${cat.icon} text-sm`}></i>
                          </span>
                        )}
                        {cat.displayName}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-4 md:px-5 py-2 md:py-2.5 bg-primary-500 text-background-50 rounded-full text-xs md:text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Itaobuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-4 md:px-5 py-2 md:py-2.5 border-b-2 border-foreground-800 text-foreground-800 text-xs md:text-sm font-medium hover:text-foreground-950 hover:border-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="mobile-hamburger-btn md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-50 border border-background-200/70 text-foreground-700 hover:border-background-300/60 transition-colors cursor-pointer"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      <div
        ref={mobileMenuRef}
        className={`fixed inset-0 z-40 md:hidden transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="absolute inset-0 bg-black/30" onClick={() => setIsMobileMenuOpen(false)}></div>
        <div className="absolute top-0 right-0 w-[300px] h-full bg-background-50 overflow-y-auto pt-20 pb-10 px-5">
          <div className="mb-6">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-home-line"></i>
              </span>
              <span className="text-foreground-800 text-sm font-medium">Home</span>
            </Link>
            <Link
              to="/articoli/shopping-internazionale-guida"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-guide-line"></i>
              </span>
              <span className="text-foreground-800 text-sm font-medium">Guida Acquisti</span>
            </Link>
          </div>

          <div className="border-t border-background-200/60 pt-5 mb-6">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-4 mb-3">
              Categorie Prodotto
            </p>
            <div className="space-y-0.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  {cat.icon && (
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                  )}
                  <span className="text-foreground-700 text-sm">{cat.displayName}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-background-200/60 pt-5">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 bg-primary-500 text-background-50 rounded-xl hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50/20">
                <i className="ri-shopping-bag-line"></i>
              </span>
              <span className="text-sm font-semibold">Itaobuy Sheet</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}